import {
	hasIdentifierText,
	normalizeIdentifierValue
} from "../identifierRows";

export type IdentifierId = string | number;

export interface IdentifierRecord {
	Id?: IdentifierId;
	Type?: { Code?: string };
	Value?: unknown;
	IsActive?: boolean;
}

export interface IdentifierUpdateOperation {
	id: IdentifierId;
	typeCode: string;
	value: string;
}

export interface IdentifierStateChangeOperation extends IdentifierUpdateOperation {
	toActive: boolean;
}

export interface IdentifierCreateOperation {
	typeCode: string;
	value: string;
}

export interface IdentifierOperationPlan {
	updates: IdentifierUpdateOperation[];
	stateChanges: IdentifierStateChangeOperation[];
	creates: IdentifierCreateOperation[];
	total: number;
}

export interface IdentifierEditPermissions {
	canCreate(typeCode: string): boolean;
	canUpdate(typeCode: string): boolean;
}

export class IdentifierEditPlan {
	private readonly originalIdentifiersById: Map<IdentifierId, IdentifierRecord>;

	public constructor(
		originalIdentifiers: ReadonlyArray<IdentifierRecord>,
		public readonly stagedRows: IdentifierRecord[],
		private readonly permissions: IdentifierEditPermissions
	) {
		this.originalIdentifiersById = new Map(
			originalIdentifiers.map(identifier => [identifier.Id as IdentifierId, identifier])
		);
	}

	public get isDirty(): boolean {
		return this.operationPlan.total > 0;
	}

	public get operationPlan(): IdentifierOperationPlan {
		const updates: IdentifierUpdateOperation[] = [];
		const stateChanges: IdentifierStateChangeOperation[] = [];
		const creates: IdentifierCreateOperation[] = [];
		const stagedRows = this.stagedRows.filter(row => hasIdentifierText(row.Type?.Code));

		for(const row of stagedRows.filter(candidate => candidate.Id !== undefined)) {
			const originalIdentifier = this.originalIdentifiersById.get(row.Id as IdentifierId);
			if(!originalIdentifier) {
				continue;
			}

			const typeCode = row.Type?.Code as string;
			const originalValue = normalizeIdentifierValue(originalIdentifier.Value);
			const currentValue = normalizeIdentifierValue(row.Value);
			const valueChanged = currentValue !== originalValue && hasIdentifierText(currentValue);

			if(valueChanged && this.permissions.canUpdate(typeCode)) {
				updates.push({
					id: row.Id as IdentifierId,
					typeCode,
					value: currentValue
				});
			}

			if(originalIdentifier.IsActive !== row.IsActive) {
				stateChanges.push({
					id: row.Id as IdentifierId,
					toActive: row.IsActive === true,
					typeCode,
					value: valueChanged ? currentValue : originalValue
				});
			}
		}

		const uniqueCreateKeys = new Set<string>();
		for(const row of stagedRows.filter(candidate => candidate.Id === undefined)) {
			const typeCode = row.Type?.Code as string;
			const value = normalizeIdentifierValue(row.Value);

			if(!row.IsActive
				|| !hasIdentifierText(value)
				|| !this.permissions.canCreate(typeCode)) {
				continue;
			}

			const key = `${typeCode}::${value}`;
			if(uniqueCreateKeys.has(key)) {
				continue;
			}

			const hasExistingDuplicate = stagedRows.some(existingRow =>
				existingRow.Id !== undefined
					&& existingRow.IsActive
					&& existingRow.Type?.Code === typeCode
					&& normalizeIdentifierValue(existingRow.Value) === value
			);

			if(hasExistingDuplicate) {
				continue;
			}

			uniqueCreateKeys.add(key);
			creates.push({ typeCode, value });
		}

		return {
			updates,
			stateChanges,
			creates,
			total: updates.length + stateChanges.length + creates.length
		};
	}
}
